import React, { useEffect, useState, useCallback, useContext } from 'react';
import { customerSVC as Customer, subscriptionsSVC } from '@com/core/common/services/api';
import { TAddress } from '@com/core/common/types/customer_address';
import { TCustomer, TLoyalty, TReturnsTrackingResponse } from '@com/core/common/types/customer';
import { CartContext } from '@com/core/common/contexts/cartContext';
import { DailyContext } from '@com/core/common/contexts/dailyContext';
import { Router } from '@com/core/common/translations/i18n';
import { reportError, reportEvent } from '@com/core/common/utils/sentry';
import { redirect } from '@com/core/common/utils/url';
import Analytics from '@com/core/common/utils/analytics';
import isString from 'lodash/isString';
import dynamic from 'next/dynamic';
import { useSnackbar } from 'notistack';
import { TLocale } from '../types/locale';
import { TDeliveryTimeSlot } from '../types/cartDaily';
import { TProduct } from '../types/catalog';

const CallbackModal = dynamic(() => import('../components/account/common/CallbackModal'));
const Authentication = dynamic(() => import('@com/core/common/components/login/Authentication'));

type TLoginUI = 'login' | 'sign-up' | 'reset' | 'login-schmaily' | 'sign-up-schmaily' | 'reset-schmaily';
type TProps = {
    children: JSX.Element;
    extras?: ['loyalty'];
    mandatoryLogin?: boolean;
    locale: TLocale;
};

type TProductToAdd = {
    offerCode: string;
    quantity: number;
    sku: string;
    showModal: boolean;
    eventLocation: string;
    attachments: string[];
};

export type TCustomerContextState = {
    addresses: {
        data: TAddress[] | null;
        error: string | null;
        isLoading: boolean;
        changeStatus: boolean;
        changeError: string | null;
        changeLoading: boolean;
        showAddressModal: boolean;
        isQuickAddToCart?: boolean;
        productToAdd?: TProductToAdd;
    };
    customer: TCustomer | null;
    customerError: string | null;
    isCustomerLoading: boolean;
    isSignedIn: boolean;
    isCustomerLoginFailed: boolean;
    loyalty: TLoyalty | null;
    callBackModalOpen: boolean;
    isReturnsTrackingModalOpen: boolean;
    trackedReturnId: string | null;
    returnsTrackingData: TReturnsTrackingResponse | null;
    loginError: string | null;
    isLoadingLogin: boolean;
    passwordResetEmailSent: boolean;
    isModalOpen: boolean;
    subscriptions: {
        subscriptionsData: {
            isSubEnabled: boolean;
            preferences: {
                address: TAddress;
                availableCredit: number;
                card: null; // Arsalan pending
                deliveryTimeSlot: TDeliveryTimeSlot;
                isCreditOnly: boolean;
                notifications: {
                    email: boolean;
                    pn: boolean;
                    sms: boolean;
                };
            };
            skuEnricher: any;
            subCount: number;
            subPause: any;
            subPauseModalData: {
                maxEndDate: string;
                maxStartDate: string;
                minStartDate: string;
                reasons: [
                    {
                        code: string;
                        name: string;
                    },
                ];
            };
            subsProduct: TProduct[];
        };
        get: () => Promise<void>;
        set: (data: any) => void;
        toggleIsSubscriptionsPage: (isSubPage: boolean) => Promise<void>;
        isSubscriptionsPage: boolean;
        isSubscriptionsLoading: boolean;
    };
};

type TCustomerContextFunctions = {
    addresses: {
        get: () => Promise<void>;
        changeOrderAddress: (orderNr: string, addressId: number) => Promise<void>;
        changeOrderAddressClear: () => void;
        toggleShowAddressModal: (show: boolean, isSub?: boolean, productObjToAdd?: TProductToAdd) => void;
    };
    getCustomer: () => Promise<void>;
    handleSignoutAndRedirect: () => Promise<void>;
    logout: () => void;
    openCallBackModal: () => void;
    toggleReturnsTrackingModal: () => void;
    handleReturnsTrackingRequest: (trackingId: string) => void;
    getReturnsTrackingData: (returnNr: string) => void;
    showLoginModal: (state: boolean) => void;
    setInitialLoginScreen: (state: TLoginUI) => void;
    clearLoginError: () => void;
    doLogin: (email: string, password: string) => void;
    doReset: (email: string) => void;
    clearReset: () => void;
    doSignUp: (firstName: string, lastName: string, email: string, password: string) => void;
};

const initContextState: TCustomerContextState & TCustomerContextFunctions = {
    addresses: {
        data: null,
        error: null,
        isLoading: false,
        changeStatus: false,
        changeError: null,
        changeLoading: false,
        get: async (): Promise<void> => undefined,
        changeOrderAddress: async (): Promise<void> => undefined,
        // adding a new address goes here
        changeOrderAddressClear: (): void => undefined,
        showAddressModal: false,
        isQuickAddToCart: false,
        productToAdd: undefined,
        toggleShowAddressModal: (): void => undefined,
    },
    subscriptions: {
        isSubEnabled: false,
        preferences: {
            address: null,
            availableCredit: 0,
            card: null, // Arsalan pending
            deliveryTimeSlot: null,
            isCreditOnly: false,
            notifications: {
                email: false,
                pn: false,
                sms: false,
            },
        },
        skuEnricher: null,
        subCount: 0,
        subPause: null,
        subPauseModalData: null,
        subsProduct: null,
        get: async (): Promise<void> => undefined,
        set: () => undefined,
        toggleIsSubscriptionsPage: async (isPageSub: boolean): promise<void> => undefined,
        isSubscriptionsPage: false,
        isSubscriptionsLoading: false,
    },
    customer: null,
    customerError: null,
    isSignedIn: false,
    isCustomerLoading: true,
    loyalty: null,
    callBackModalOpen: false,
    isReturnsTrackingModalOpen: false,
    trackedReturnId: null,
    returnsTrackingData: null,
    loginError: null,
    isLoadingLogin: false,
    passwordResetEmailSent: false,
    isModalOpen: false,
    openCallBackModal: (): void => undefined,
    getCustomer: async (): Promise<void> => undefined,
    handleSignoutAndRedirect: (): Promise<void> => Promise.resolve(undefined),
    logout: (): void => undefined,
    toggleReturnsTrackingModal: (): void => undefined,
    handleReturnsTrackingRequest: (): void => undefined,
    getReturnsTrackingData: (): void => undefined,
    showLoginModal: (): void => undefined,
    setInitialLoginScreen: (): void => undefined,
    clearLoginError: (): void => undefined,
    doLogin: () => undefined,
    doReset: () => undefined,
    clearReset: () => undefined,
    doSignUp: () => undefined,
};

export const CustomerContext = React.createContext(initContextState);

export const CustomerContextProvider = ({ children, extras, locale, mandatoryLogin }: TProps): React.ReactElement => {
    const [customer, setCustomer] = useState<TCustomerContextState['customer']>(null);
    const [isCustomerLoading, setIsCustomerLoading] = useState<TCustomerContextState['isCustomerLoading']>(true);
    const [customerError, setCustomerError] = useState<TCustomerContextState['customerError']>(null);

    // -- Address States
    const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
    const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
    const [errorAddresses, setErrorAddresses] = useState<string | null>(null);
    const [addresses, setAddresses] = useState<TCustomerContextState['addresses']['data']>(null);
    const [errorAddressChange, setErrorAddressChange] = useState<string | null>(null);
    const [isLoadingAddressChange, setIsLoadingAddressChange] = useState(false);
    const [statusAddressChange, setStatusAddressChange] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [isQuickAddToCart, setIsQuickAddToCart] = useState(false);
    const [productToAdd, setProductToAdd] = useState<TProductToAdd | undefined | null>(null);
    const [isSubscriptionsPage, setIsSubscriptionsPage] = useState(false);

    // --- Login States
    const [isLoadingLogin, setIsLoadingLogin] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [initialLoginScreen, setInitialLoginScreen] = useState<TLoginUI>('login');
    const [passwordResetEmailSent, setPasswordResetEmailSent] = useState(false);
    const [isSignedIn, setIsSignedIn] = useState(false);
    const [isCustomerLoginFailed, setIsCustomerLoginFailed] = useState(false);

    // --- Loyalty
    const [loyalty, setLoyalty] = useState<TCustomerContextState['loyalty']>(null);
    const [callBackModalOpen, setCallBackModalOpen] = useState(false);

    // --- Returns Tracking
    const [returnsTrackingData, setReturnsTrackingData] = useState<TCustomerContextState['returnsTrackingData']>(null);
    const [isReturnsTrackingModalOpen, setIsReturnsTrackingModalOpen] = useState(false);
    const [trackedReturnId, setTrackedReturnId] = useState<string | null>(null);

    const [subscriptionsData, setSubscriptionsData] = useState<TCustomerContextState['subscriptions']>();

    const { enqueueSnackbar } = useSnackbar();

    const { getCart } = useContext(CartContext);
    const { setIsDailyCheckRequired } = useContext(DailyContext);

    const getAddresses = useCallback(async (): Promise<void> => {
        setIsLoadingAddresses(true);
        try {
            const { data } = await Customer(locale.apiLocale).address.get();
            setAddresses(data.data);
            setIsLoadingAddresses(false);
        } catch (error) {
            setErrorAddresses(error.error);
            setIsLoadingAddresses(false);
            reportError(error, { componentStack: 'CustomerContextProvider.getAddresses' });
        }
    }, [locale]);

    const getSubscriptions = async (): Promise<void> => {
        setIsLoadingSubscriptions(true);
        try {
            const { data } = await subscriptionsSVC(locale.apiLocale).getSubscriptions('product', true);
            setSubscriptionsData(data.data);
            setIsLoadingSubscriptions(false);
            console.log('Context');
        } catch (error) {
            reportError(error, { componentStack: 'CustomerContextProvider.getSubscriptions' });
            setIsLoadingSubscriptions(false);
        }
    };

    const toggleIsSubscriptionsPage = (isSubPage: boolean) => {
        setIsSubscriptionsPage(isSubPage);
    };

    const getCustomer = useCallback(async (): Promise<void> => {
        setIsCustomerLoading(true);
        try {
            const { data } = await Customer(locale.apiLocale).profile.get();
            setCustomer(data.data as TCustomer);
            setIsCustomerLoginFailed(false);
            setIsCustomerLoading(false);
            const responseData = data.data;
            Analytics.trackUser(locale, {
                event: 'user_logged_in',
                email: responseData.email,
                emailSubscribed: responseData.isSubscribed,
                firstName: responseData.firstName,
                lastName: responseData.lastName,
                customerCode: responseData.id,
                uid: responseData.id,
                custId: responseData.id,
                sourceMpCode: responseData.sourceMpCode,
                join_date: responseData.joinDate,
                visitor_id: Analytics.visitorId || '',
                visitorId: Analytics.visitorId || '',
                subscriptionKey: responseData.subscriptionKey,
            });
            // ****** Leaving these 👇 commented untill the Utils are done ******
            // captureBreadcrumb('Fetched customer data', customer);
            // configureScope({
            //     id: response.data.id,
            //     email: response.data.email,
            // });
            // updateUserId(response.data.id);
            // trackCustomer('in', props.locale);

            // if (Helper.isBrowser) {
            //     (window as WindowType).AppUser = {
            //         ...response.data,
            //     };
            // }
        } catch (error) {
            if (isString(error)) {
                // Set error directly if string
                setCustomerError(error);
            } else if (error.error && isString(error.error)) {
                // If error is sent within the response, set that instead
                setCustomerError(error.error);

                if (error.status === 403) {
                    setIsCustomerLoginFailed(true);
                }
            } else {
                // If things are funky, either set the error.error or error itself, stringified
                setCustomerError(JSON.stringify(error || error.error));
            }
            if (error.status === 403 && mandatoryLogin) {
                setShowLoginModal(true);
            }
            setIsCustomerLoading(false);
            reportError(error, { componentStack: 'CustomerContextProvider.getCustomer' });
        }
    }, [locale, mandatoryLogin]);

    const getLoyalty = async (): Promise<void> => {
        try {
            const { data } = await Customer(locale.apiLocale).loyalty.get();

            setLoyalty(data.data as TLoyalty);
        } catch (error) {
            reportError(error, { componentStack: 'CustomerContextProvider.getLoyalty' });
        }
    };

    const openCallBackModal = (): void => {
        setCallBackModalOpen(true);
    };

    const closeCallBackModal = (): void => {
        setCallBackModalOpen(false);
    };

    useEffect(() => {
        setIsSignedIn(!!customer?.email);
    }, [customer]);

    useEffect(() => {
        getCustomer();
        if (extras && extras.includes('loyalty')) {
            getLoyalty();
        }
    }, [getCustomer, extras]);

    const doLogin = useCallback(
        async (email: string, password: string) => {
            setIsLoadingLogin(true);
            try {
                const { data } = await Customer(locale.apiLocale).user.postLogin({
                    email,
                    password,
                });

                const { data: responseData } = data;

                getCustomer();
                if (extras?.includes('loyalty')) {
                    getLoyalty();
                }
                getCart(false);
                setIsLoadingLogin(false);
                setCustomerError(null);
                setLoginError(null);
                setShowLoginModal(false);
                setIsDailyCheckRequired(true);

                // ****** Leaving these 👇 commented untill the Utils are done ******
                // captureBreadcrumb('Fetched customer data', customer);
                // configureScope({
                //     id: response.data.id,
                //     email: response.data.email,
                // });
                // updateUserId(response.data.id);
                // trackCustomer('in', props.locale);

                // if (Helper.isBrowser) {
                //     (window as WindowType).AppUser = {
                //         ...response.data,
                //     };
                // }

                Analytics.trackUser(locale, {
                    event: 'user_sign_in',
                    email: responseData.email,
                    emailSubscribed: responseData.isSubscribed,
                    firstName: responseData.firstName,
                    lastName: responseData.lastName,
                    customerCode: responseData.id,
                    uid: responseData.id,
                    custId: responseData.id,
                    sourceMpCode: responseData.sourceMpCode,
                    join_date: responseData.joinDate,
                    visitor_id: Analytics.visitorId || '',
                    visitorId: Analytics.visitorId || '',
                    subscriptionKey: responseData.subscriptionKey,
                });
            } catch (error) {
                let errorMessage = 'Could not log in';
                if (typeof error === 'string') {
                    // Set error directly if string
                    errorMessage = error;
                } else if (error.error && typeof error.error === 'string') {
                    // If error is sent within the response, set that instead
                    errorMessage = error.error;
                } else {
                    // If things are funky, either set the error.error or error itself, stringified
                    errorMessage = JSON.stringify(error || error.error);
                }
                setLoginError(errorMessage);
                enqueueSnackbar(errorMessage, { variant: 'error' });
                setIsLoadingLogin(false);

                reportEvent('Failed login attempt', 'doLogin', 'info', { errorMessage, error });
            }
        },
        [locale, getCustomer],
    );

    const doSignUp = useCallback(
        async (firstName: string, lastName: string, email: string, password: string) => {
            setIsLoadingLogin(true);
            try {
                const response = await Customer(locale.apiLocale).user.postSignUp({
                    firstName,
                    lastName,
                    email,
                    password,
                });
                const customerData: TCustomer = response && response.data && response.data.data;
                getCustomer();
                setIsLoadingLogin(false);
                setCustomerError(null);
                setLoginError(null);
                setShowLoginModal(false);
                Analytics.trackUser(locale, {
                    event: 'user_sign_up',
                    email: customerData.email,
                    emailSubscribed: customerData.isSubscribed,
                    firstName: customerData.firstName,
                    lastName: customerData.lastName,
                    customerCode: customerData.id,
                    custId: customerData.id,
                    sourceMpCode: customerData.sourceMpCode,
                    uid: customerData.id,
                    visitor_id: Analytics.visitorId || '',
                    visitorId: Analytics.visitorId || '',
                    join_date: customerData.joinDate,
                    subscriptionKey: customerData.subscriptionKey,
                });
            } catch (error) {
                if (typeof error === 'string') {
                    setLoginError(error);
                } else if (error.error && typeof error.error === 'string') {
                    setLoginError(error.error);
                } else {
                    setLoginError(JSON.stringify(error || error.error));
                }
                setIsLoadingLogin(false);

                reportError(error, { componentStack: 'CustomerContextProvider.doSignUp' });
            }
        },
        [locale, getCustomer],
    );

    const doReset = useCallback(
        async (email: string) => {
            setIsLoadingLogin(true);
            try {
                await Customer(locale.apiLocale).user.postReset({
                    email,
                });
                setPasswordResetEmailSent(true);
                setIsLoadingLogin(false);
            } catch (error) {
                if (typeof error === 'string') {
                    setLoginError(error);
                } else if (error.error && typeof error.error === 'string') {
                    setLoginError(error.error);
                } else {
                    setLoginError(JSON.stringify(error || error.error));
                }
                setIsLoadingLogin(false);

                reportError(error, { componentStack: 'CustomerContextProvider.doReset' });
            }
        },
        [locale],
    );

    const clearReset = () => {
        setPasswordResetEmailSent(false);
    };

    const logout = async (): Promise<void> => {
        try {
            await Customer(locale.apiLocale).user.postLogout();
            setCustomer(null);
            setIsCustomerLoading(false);
            getCart(false);
            setIsDailyCheckRequired(true);
            if (Router.pathname !== '/') {
                Router.push('/');
            }
            Analytics.trackUser(locale, {
                event: 'user_sign_out',
                email: customer?.email,
                emailSubscribed: customer?.isSubscribed,
                firstName: customer?.firstName,
                lastName: customer?.lastName,
                customerCode: customer?.id,
                custId: customer?.id,
            });
        } catch (error) {
            setCustomerError(error);
            setIsCustomerLoading(false);

            reportError(error, { componentStack: 'CustomerContextProvider.logout' });
        }
    };

    const handleSignoutAndRedirect = async (): Promise<void> => {
        await logout();
        redirect(locale.isoLocale);
    };

    const updateOrderAddress = useCallback(
        async (orderNr: string, addressId: number): Promise<void> => {
            setIsLoadingAddressChange(true);
            try {
                const { data } = await Customer(locale.apiLocale).address.updateOrderAddress(orderNr, addressId);
                if (data.ok) {
                    setStatusAddressChange(true);
                } else {
                    setStatusAddressChange(false);
                    setErrorAddressChange('Could not update address!');
                }
                setIsLoadingAddressChange(false);
            } catch (error) {
                setIsLoadingAddressChange(false);
                setStatusAddressChange(false);
                setErrorAddressChange(error.error);
                reportError(error, { componentStack: 'CustomerContextProvider.updateOrderAddress' });
            }
        },
        [locale],
    );

    const clearOrderAddressChangeStates = (): void => {
        setIsLoadingAddressChange(false);
        setErrorAddressChange(null);
        setStatusAddressChange(false);
    };

    const toggleReturnsTrackingModal = (): void => {
        setIsReturnsTrackingModalOpen(openState => !openState);
    };

    const handleReturnsTrackingRequest = (trackingId: string): void => {
        setTrackedReturnId(trackingId);
        toggleReturnsTrackingModal();
    };

    const getReturnsTrackingData = useCallback(
        async (returnNr: string): Promise<void> => {
            try {
                const { data } = await Customer(locale.apiLocale).return.track(returnNr);
                setReturnsTrackingData(data);
            } catch (error) {
                reportError(error, { componentStack: 'CustomerContextProvider.getReturnsTrackingData' });
            }
        },
        [locale],
    );

    const clearLoginError = (): void => {
        setLoginError(null);
    };

    const toggleShowAddressModal = (show: boolean, isQuick = false, productObjToAdd?: TProductToAdd | null) => {
        setShowAddressModal(show);
        setIsQuickAddToCart(isQuick);
        setProductToAdd(isQuick ? productObjToAdd : null);
    };

    const customerContextValue = {
        // ------state--------
        addresses: {
            data: addresses,
            error: errorAddresses,
            isLoading: isLoadingAddresses,
            changeStatus: statusAddressChange,
            changeError: errorAddressChange,
            changeLoading: isLoadingAddressChange,
            get: getAddresses,
            changeOrderAddress: updateOrderAddress,
            changeOrderAddressClear: clearOrderAddressChangeStates,
            showAddressModal,
            isQuickAddToCart,
            productToAdd,
            toggleShowAddressModal,
        },
        subscriptions: {
            subscriptionsData,
            get: getSubscriptions,
            set: setSubscriptionsData,
            toggleIsSubscriptionsPage,
            isSubscriptionsPage,
            isSubscriptionsLoading: isLoadingSubscriptions,
        },
        customer,
        customerError,
        errorAddresses,
        isSignedIn,
        isCustomerLoading,
        loyalty,
        initialLoginScreen,
        callBackModalOpen,
        isReturnsTrackingModalOpen,
        trackedReturnId,
        returnsTrackingData,
        loginError,
        isLoadingLogin,
        passwordResetEmailSent,
        isModalOpen: showLoginModal,
        // ------functions----
        getCustomer,
        updateOrderAddress,
        handleSignoutAndRedirect,
        logout,
        openCallBackModal,
        toggleReturnsTrackingModal,
        handleReturnsTrackingRequest,
        getReturnsTrackingData,
        showLoginModal: setShowLoginModal,
        setInitialLoginScreen,
        clearLoginError,
        doLogin,
        doReset,
        doSignUp,
        clearReset,
        isCustomerLoginFailed,
    };

    return (
        <CustomerContext.Provider value={customerContextValue}>
            {children}

            {showLoginModal && <Authentication />}

            {loyalty && loyalty.isCallbackEnabled && (
                <CallbackModal callBackModalOpen={callBackModalOpen} closeModal={closeCallBackModal} />
            )}
        </CustomerContext.Provider>
    );
};
